import React, { useState, useEffect, useMemo } from 'react';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory } from '../utils/i18n';
import { 
  GraduationCap, BookOpen, Microscope, Award, LineChart, 
  Download, Copy, Check, ArrowLeft, Sparkles, FileText, 
  Upload, RefreshCw, FileCheck, X, Pill, ShieldCheck, Activity
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import { getFieldLabel, getFieldValue, getNotesText } from '../utils/demoPresetsI18n';

interface ScienceAgentViewProps {
  agentId: string;
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
}

const DEFAULT_SCIENCE_DEMO: Record<string, {
  field1LabelUk: string; field1Value: string;
  field2LabelUk: string; field2Value: string;
  field3LabelUk: string; field3Value: string;
  field4LabelUk: string; field4Value: string;
  field5LabelUk: string; field5Value: string;
  field6LabelUk: string; field6Value: string;
  notesText: string;
}> = {
  'scientific-methodology-goal-architect': {
    field1LabelUk: 'Поставлена Мета / Досліджуване Питання',
    field1Value: 'Підвищення міцності та зносостійкості композитних елементів на 40% шляхом лазерного мікролегування графен-керамічними наночастинками',
    field2LabelUk: 'Об\'єкт & Предмет Наукового Дослідження',
    field2Value: 'Об\'єкт: Процес лазерної обробки титанових сплавів / Предмет: Закономірності формування міжкристалітної наноструктури та втомна міцність',
    field3LabelUk: 'Формулювання Наукових Гіпотез (H0 / H1)',
    field3Value: 'H0: Додавання графену не змінює границю витривалості; H1: Дозування 0.35% графену забезпечує приріст циклічної міцності на 42% (p < 0.001)',
    field4LabelUk: 'Методологічний Дизайн (DoE / Методи)',
    field4Value: 'Design of Experiments (Повнофакторний експеримент 2^k), Рентгеноструктурний аналіз XRD, СЕМ-мікроскопія, дисперсійний аналіз ANOVA',
    field5LabelUk: 'Критерії Верифікації (Поппер & Falsifiability)',
    field5Value: 'Критерій спростування: Падіння ударної в\'язкості KCV нижче 35 Дж/см2 при мікротвердості HV > 650 або наявність мікропор > 5 мкм',
    field6LabelUk: 'Цільовий Рівень Готовності (TRL) & Термін',
    field6Value: 'TRL 3 (Лабораторне доведення) → TRL 6 (Валідація прототипу в експлуатаційних умовах) / 12 місяців',
    notesText: 'Застосувати строгу методологію наукового дослідження для вирішення інженерно-технологічного завдання. Здійснити формалізацію проблеми за принципом Карла Поппера, декомпонувати наукові гіпотези, розробити матрицю планування повнофакторного експерименту (DoE), провести критичний аналіз джерел (Systematic Literature Review) за Scopus/WoS, виявити когнітивні та експериментальні упередження, побудувати поетапну дорожню карту валідації (Roadmap TRL 3-6) та сформувати готовий Науково-Методологічний Паспорт Дослідження у Word (.docx) за ДСТУ 4163:2020.'
  },
  'pharma-eu-dossier-ectd-validator': {
    field1LabelUk: 'Торгова назва ЛЗ, МНН & Лікарська форма',
    field1Value: 'КАРДІОПРОТЕКТ (Rivaroxaban / Рівароксабан) таблетки вкриті оболонкою 20 мг',
    field2LabelUk: 'Цільовий регуляторний орган & Процедура',
    field2Value: 'Державний експертний центр МОЗ України (ДЕЦ) + Польський URPL (Національна / Decentralised Procedure)',
    field3LabelUk: 'Формат та версія реєстраційного досьє eCTD',
    field3Value: 'eCTD v3.2.2 (Модулі 1-5: Регіональний, Огляди, Хімія/CMC, Доклінічні та Клінічні звіти)',
    field4LabelUk: 'Статус сертифікації виробництва GMP',
    field4Value: 'Сертифікат EU GMP (EudraGMDP) виданий GIF Poland + Висновок ДЛС України',
    field5LabelUk: 'Статус гармонізації інструкцій (SmPC / PIL)',
    field5Value: 'Підготовлено проєкт Короткої характеристики (SmPC) та Листка-вкладиша (PIL) за шаблоном QRD',
    field6LabelUk: 'Обґрунтування еквівалентності (Bioequivalence)',
    field6Value: 'Звіт про дослідження біоеквівалентності (BE Study in vivo) vs оригінальний препарат Xarelto',
    notesText: 'Здійснити комплексну технічну та регуляторну валідацію реєстраційного досьє лікарського засобу у форматі eCTD (Модулі 1–5). Перевірити структуру XML-маніфесту, відповідність інструкції для застосування (SmPC/PIL) європейським вимогам EMA та ДЕЦ МОЗ, оцінити наявність звітів біоеквівалентності та стабільності (ICH Q1A) та сформувати Регуляторний звіт зауважень (Validation Deficiency Letter) у Word (.docx) за ДСТУ 4163:2020.'
  },
  'science-grant-proposal-writer': {
    field1LabelUk: 'Назва конкурсу грантів & Програма', field1Value: 'Horizon Europe (Cluster 4: Digital, Industry & Space) / НФФД',
    field2LabelUk: 'Назва та гіпотеза наукового проєкту', field2Value: 'Інтелектуальні композитні матеріали на основі графену для автономних БПЛА',
    field3LabelUk: 'Цільовий обсяг фінансування (EUR / UAH)', field3Value: '1 200 000 EUR (Термін реалізації: 36 місяців)',
    field4LabelUk: 'Кількість установ-партнерів у консорціумі', field4Value: '5 партнерів (Україна, Німеччина, Польща, Франція, Швеція)',
    field5LabelUk: 'Планова кількість публікацій Scopus & Патентів', field5Value: '8 статей Scopus (Q1/Q2) + 2 патенти ЄП',
    field6LabelUk: 'Рівень технологічної готовності (TRL)', field6Value: 'TRL 3 (Лабораторний прототип) → TRL 6 (Демонстрація в умовах)',
    notesText: 'Скласти конкурсну заявку Horizon Europe: підготувати розділ «Excellence» (наукова новизна, цілі, методологія), розділ «Impact» (економічний та захисний ефект) та розділ «Implementation» із розрахунком трудомісткості FTE науковців, Work Packages та графіком Гантта.'
  },
  'science-paper-peer-reviewer': {
    field1LabelUk: 'Назва наукової статті & Робоча тема', field1Value: 'Deep Learning Architectures for Real-Time Satellite Synthetic Aperture Radar (SAR) Segmentation',
    field2LabelUk: 'Цільовий журнал & Квартиль (Scopus / WoS)', field2Value: 'Remote Sensing of Environment (Elsevier, Scopus Q1, IF = 13.5)',
    field3LabelUk: 'Наукова галузь & Галузь знань', field3Value: '12 Комп\'ютерні науки / Remote Sensing & GIS',
    field4LabelUk: 'Обсяг вибірки / Експериментальні дані', field4Value: '14 500 кадрів Sentinel-1 C-band SAR (BBOX Україна & Крим)',
    field5LabelUk: 'Бажаний стиль оформлення бібліографії', field5Value: 'IEEE Reference Style / Elsevier Reference Standard',
    field6LabelUk: 'Основна методологія & Модель ШІ', field6Value: 'U-Net з бекбоном ResNet-101 та меканізмом Attention',
    notesText: 'Провести рецензування наукової статті за критеріями журналу Scopus Q1: аудитувати структуру IMRaD, вичитати термінологію та граматику Academic English, оцінити статистичну значущість p-value та відформатувати список з 35 джерел літератури за стандартом IEEE.'
  },
  'science-patent-prior-art-analyst': {
    field1LabelUk: 'Назва винайдення / корисної моделі', field1Value: 'Спосіб диференційованого орієнтування антеної решітки супутникового термографа',
    field2LabelUk: 'Галузь техніки за МПК (Міжнародна патентна класифікація)', field2Value: 'H04B 7/185 (Супутниковий зв\'язок), G01S 13/95 (Радіолокація)',
    field3LabelUk: 'Бази даних для патентного пошуку', field3Value: 'УКРНОІВІ (СЕВ ЗЕС), ESPACENET (EPO), WIPO PATENTSCOPE, USPTO',
    field4LabelUk: 'Найближчий аналог та прототип', field4Value: 'Патент США US10842910B2 (Прототип з кутовим кроком 0.5°)',
    field5LabelUk: 'Технічний результат & Перевага винаходу', field5Value: 'Підвищення точності детекції температурного азимуту на 34%',
    field6LabelUk: 'Цільове відомство для подачі заявки', field6Value: 'Державна організація «УКРНОІВІ» (Україна, СЕВ ЗЕС)',
    notesText: 'Провести патентний пошук на патентну чистоту за світовими базами даних, сформулювати патентну формулу з незалежними та залежними пунктами (Claims), підготувати опис винаходу за вимогами УКРНОІВІ та скласти заяву на видачу патенту України.'
  },
  'science-phd-dissertation-auditor': {
    field1LabelUk: 'Тема дисертаційної роботи PhD', field1Value: 'Науково-методологічні та технологічні засади створення Національної інтегрованої системи космічного моніторингу («НІС-ЕКО»)',
    field2LabelUk: 'Здобувач, Керівник & Наукова Установа', field2Value: 'Банах А.С. / Керівник: д.т.н., с.н.с. Присяжний В.І. / ДНУ «ІЕВРУ»',
    field3LabelUk: 'Наукова спеціальність & Нормативна база', field3Value: '183 Технології захисту довкілля (103 / 122) / Наказ МОН № 40, ПКМУ № 44 (№ 502)',
    field4LabelUk: 'Кількість наукових публікацій здобувача', field4Value: '8 публікацій (3 Scopus Q1/Q2, 5 фахових категорії «Б» України) + 1 патент',
    field5LabelUk: 'Вимоги до апробації & Академічна доброчесність', field5Value: '6 міжнародних конференцій / Унікальність 91.5% (Unicheck / Turnitin)',
    field6LabelUk: 'Статус підготовки до разової ради', field6Value: 'Готовий канонічний рукопис (Вступ, Розділи 1–4, Висновки, Додатки за ДСТУ 8302)',
    notesText: 'Провести комплексний експертний аудит та нормоконтроль дисертації PhD за Наказом МОН № 40 та ПКМУ № 44 (№ 502): перевірити відповідність 4 пунктів наукової новизни, достовірність формул UKF Data Fusion, практичні результати для ДКАУ та Міндовкілля, сформувати висновок про наукову новизну та проєкт Автореферату у Word (.docx) за ДСТУ 4163:2020.'
  },
  'science-data-statistician-r-python': {
    field1LabelUk: 'Тип дослідження & Експериментальна вибірка', field1Value: 'Клініко-біологічне дослідження / 240 лабораторних зразків',
    field2LabelUk: 'Основні змінні та параметри спостереження', field2Value: 'Концентрація біомаркера X, вік, доза препарату Y, рівень pH',
    field3LabelUk: 'Необхідні статистичні критерії (Tests)', field3Value: 'Shapiro-Wilk (нормальність), ANOVA / Kruskal-Wallis, p-value',
    field4LabelUk: 'Формат вихідного файлу даних', field4Value: 'CSV / Excel-таблиця з 240 рядками та 18 колонками',
    field5LabelUk: 'Рівень значущості (alpha threshold)', field5Value: 'p < 0.05 (Довірчий інтервал 95% CI)',
    field6LabelUk: 'Платформа обробки (R пакети / Python scipy)', field6Value: 'R (ggplot2, stats, tidyverse) / Python (SciPy, Seaborn)',
    notesText: 'Провести повну статистичну обробку експериментальних даних в R/Python: перевірити розподіл на нормальність, розрахувати параметричний t-критерій та непараметричний U-критерій Манна-Уїтні, побудувати графіки Boxplot з маркерами значущості p-value та сформувати статистичний розділ статті.'
  },
  'nazavo-accreditation-auditor': {
    field1LabelUk: 'Назва Освітньої Програми (ОП) & Рівень', field1Value: 'ОП «Комп’ютерні науки та штучний інтелект» (Магістр, 122)',
    field2LabelUk: 'Гарант освітньої програми & Кафедра', field2Value: 'д.т.н., професор Шевченко О.І. / Кафедра системного аналізу',
    field3LabelUk: 'Обсяг програми (Кредити ECTS / Років)', field3Value: '90 кредитів ECTS (1 рік 4 місяці навчання)',
    field4LabelUk: 'Кількість науково-педагогічних працівників (НПП)', field4Value: '12 НПП (8 докторів наук, 4 кандидати наук, 100% за фахом)',
    field5LabelUk: 'Наявність закордонного стажування & ПРН', field5Value: '8 викладачів пройшли стажування у ЄС / 16 ПРН оновлено у 2025 р.',
    field6LabelUk: 'Бази практик & Працедавці-партнери', field6Value: '6 договорів з IT-компаніями (EPAM, SoftServe, GlobalLogic)',
    notesText: 'Провести повний експертний аудит Відомостей про самооцінювання освітньої програми «Комп’ютерні науки» за 9 критеріями НАЗЯВО. Перевірити відповідність матриці компетентностей вимогам стандарту вищої освіти МОНУ, сформулювати сильні сторони та сформувати готовий Звіт самооцінювання у Word (.docx) за вимогами НАЗЯВО.'
  },
  'syllabus-curriculum-generator': {
    field1LabelUk: 'Назва навчальної дисципліни & Код', field1Value: 'Інтелектуальний аналіз даних та Machine Learning (ОК 14)',
    field2LabelUk: 'Обсяг у кредитах ECTS / Годин', field2Value: '5 кредитів ECTS (150 годин: 30 год лекцій, 30 год лабораторій, 90 год СРС)',
    field3LabelUk: 'Курс, Семестр & Форма підсумкового контролю', field3Value: '3 курс, 5 семестр / Іспит (100-бальна шкала)',
    field4LabelUk: 'Мова викладання & Спеціальність', field4Value: 'Українська (English terms) / 121 Інженерія програмного забезпечення',
    field5LabelUk: 'Ключові програмні результати навчання (ПРН)', field5Value: 'Здатність застосовувати алгоритми ML, класифікацію, регресію та нейромережі',
    field6LabelUk: 'Програмне забезпечення & Лабораторії', field6Value: 'Python 3.12, Jupyter Notebook, PyTorch, Scikit-learn, Google Colab',
    notesText: 'Розробити повний Силлабус та Робочу програму дисципліни «Інтелектуальний аналіз даних» за вимогами МОНУ та ECTS. Розподілити навчальні теми по тижнях, сформувати завдання для лабораторних робіт, критерії оцінювання за 100-бальною та ECTS шкалою та підібрати актуальний список джерел (2024-2026 рр.) у Word (.docx) за ДСТУ 4163:2020.'
  },
  'test-bank-constructor': {
    field1LabelUk: 'Дисципліна & Назва підсумкового модуля', field1Value: 'Дисципліна «Архітектура хмарних обчислень» / Модуль № 1',
    field2LabelUk: 'Загальна кількість тестових питань у банку', field2Value: '60 тестових питань (20 легких, 30 середніх, 10 складних)',
    field3LabelUk: 'Типи тестових завдань', field3Value: 'Multiple Choice (70%), Matching (20%), Short Answer (10%)',
    field4LabelUk: 'Формат білетів / Експорту', field4Value: 'Moodle XML + GIFT + 15 Друкованих білетів з ключами',
    field5LabelUk: 'Наявні джерела / Конспект лекцій', field5Value: 'Завантажено конспект лекцій з теми «Хмарні сервіси AWS & Docker»',
    field6LabelUk: 'Рівень штрафу за невірно обрані дистрактори', field6Value: 'Без штрафних балів / Чіткі підказки до відповідей',
    notesText: 'Згенерувати банк з 60 тестових питань за наданим конспектом лекцій з Хмарних обчислень. Для кожного питання підібрати 4 правдоподібні дистрактори, вказати ключ правильної відповіді, розрахувати бали та згенерувати готові файли для імпорту в Moodle (XML / GIFT) та друкований комплект білетів у Word (.docx).'
  },
  'scopus-wos-paper-auditor': {
    field1LabelUk: 'Назва статті & Автори', field1Value: 'Optimization of Distributed Power Grid Networks Using Multi-Agent Deep Reinforcement Learning',
    field2LabelUk: 'Цільовий журнал Scopus & Квартиль', field2Value: 'Applied Energy (Elsevier, Scopus Q1, Impact Factor: 11.2)',
    field3LabelUk: 'Стиль цитування бібліографії', field3Value: 'IEEE Citation Standard / APA 7th Edition',
    field4LabelUk: 'Обсяг статті (Слів / Сторінок)', field4Value: '6 800 слів / 14 сторінок (включаючи 8 рисунків та 3 таблиці)',
    field5LabelUk: 'Наявні розділи структури', field5Value: 'Повна структура IMRaD (Abstract, Intro, Methods, Results, Discussion, Conclusion)',
    field6LabelUk: 'Рівень перевірки Academic English', field6Value: 'Поглиблене редагування стилю, усунення граматичних та термінологічних помилок',
    notesText: 'Провести комплексний рецензійний аудит наукової статті під вимоги журналу Scopus Q1 (Applied Energy). Вичитати та відредагувати текст Academic English, перевірити коректність побудови методології, розрахунку p-value, оформити бібліографію за стандартом IEEE та сформувати Рецензійний висновок у Word (.docx) за ДСТУ 4163:2020.'
  },
  'nrfu-grant-proposal-builder': {
    field1LabelUk: 'Назва конкурсу грантів & Фонд', field1Value: 'Конкурс НФДУ «Передові наукові дослідження» / Horizon Europe',
    field2LabelUk: 'Тема наукового дослідження', field2Value: 'Розробка автономних геопросторових систем моніторингу критичної інфраструктури',
    field3LabelUk: 'Запитуваний бюджет гранту (грн / EUR)', field3Value: '4 500 000.00 грн (Термін виконання: 24 місяці)',
    field4LabelUk: 'Склад наукового колективу (FTE)', field4Value: '6 науковців (2 доктори наук, 3 кандидати наук, 1 аспірант)',
    field5LabelUk: 'Кількість планованих публікацій Scopus', field5Value: '6 статей у Scopus Q1/Q2 + 2 патенти України',
    field6LabelUk: 'Основні Work Packages (Робочі пакети)', field6Value: 'WP1: Теоретичні основи, WP2: Моделювання, WP3: Прототипування, WP4: Впровадження',
    notesText: 'Розробити повну грантову заявку для конкурсу НФДУ: підготувати наукове обґрунтування новизни (Excellence), описати практичний та оборонний вплив (Impact), скласти детальний кошторис витрат (заробітна плата, матеріали, обладнання) та сформувати календарний план і діаграму Гантта у Word (.docx) за ДСТУ 4163:2020.'
  },
  'phd-dissertation-compliance': {
    field1LabelUk: 'Тема PhD дисертації & Здобувач', field1Value: 'Методи та засоби інтелектуального аналізу часових рядів у геоінформаційних системах',
    field2LabelUk: 'Код та назва спеціальності', field2Value: '122 Комп’ютерні науки (Галузь 12 Інформаційні технології)',
    field3LabelUk: 'Науковий керівник & Кафедра', field3Value: 'д.т.н., проф. Бондаренко В.П. / Кафедра геоінформатики',
    field4LabelUk: 'Кількість наукових праць за темою', field4Value: '6 публікацій (2 статті Scopus Q2, 3 фахові статті України, 1 патент)',
    field5LabelUk: 'Вимоги до списку джерел', field5Value: 'ДСТУ 8302:2015 «Інформація та документація. Бібліографічне посилання»',
    field6LabelUk: 'Перевірка унікальності (Антиплагіат)', field6Value: 'Унікальність 91.2% за звітом StrikePlagiarism / Unicheck',
    notesText: 'Провести повний нормоконтроль дисертації PhD та анотації за вимогами МОНУ (Наказ № 40) та ПКМУ № 44. Перевірити формулювання 3 пунктів наукової новизни, усунути помилки оформлення рисунків/таблиць, стандартизувати список із 140 джерел за ДСТУ 8302:2015 та сформувати Акт нормоконтролю у Word (.docx).'
  },
  'thesis-formatter-dstu': {
    field1LabelUk: 'Вид студентської роботи & Спеціалізація', field1Value: 'Кваліфікаційна Магістерська робота / 121 Інженерія ПЗ',
    field2LabelUk: 'Тема дипломної роботи', field2Value: 'Розробка мікросервісної платформи автокатегоризації фінансових транзакцій',
    field3LabelUk: 'Студент & Науковий керівник', field3Value: 'ст. гр. ІПЗ-61 Мельник А.В. / к.т.н., доц. Кравченко І.М.',
    field4LabelUk: 'Кількість сторінок & Розділів', field4Value: '85 сторінок / 4 основні розділи + додатки',
    field5LabelUk: 'Стандарт оформлення полів та шрифту', field5Value: 'Times New Roman 14pt, міжрядковий 1.5, поля 30/10/20/20 мм',
    field6LabelUk: 'Стандарт бібліографічних посилань', field6Value: 'ДСТУ 8302:2015 (Національний стандарт України)',
    notesText: 'Провести повне форматирование та нормоконтроль магістерської роботи строго за ДСТУ 4163:2020 та ДСТУ 8302:2015. Налаштувати поля, абзацні відступи 12.5 мм, нумерацію формул та рисунків, впорядкувати список із 50 використаних джерел та згенерувати готовий текст дипломної роботи й анотацію у Word (.docx).'
  },
  'patent-claims-builder': {
    field1LabelUk: 'Назва винаходу / корисної моделі', field1Value: 'Спосіб імпульсно-дугового зварювання титанових сплавів у середовищі аргону',
    field2LabelUk: 'Код МПК (Патентна класифікація)', field2Value: 'B23K 9/16 (Зварювання у захисних газах), C22C 14/00 (Титанові сплави)',
    field3LabelUk: 'Патентні бази пошуку (УКРНОІВІ / WIPO)', field3Value: 'УКРНОІВІ СЕВ ЗЕС, ESPACENET (ЄП), WIPO PATENTSCOPE (Світ)',
    field4LabelUk: 'Найближчий аналог & Прототип', field4Value: 'Патент України № 148920 (Прототип з постійним струмом дуги)',
    field5LabelUk: 'Технічний результат & Новизна розробки', field5Value: 'Зменшення пористості зварного шва на 42% та підвищення міцності на 18%',
    field6LabelUk: 'Заявник & Автори винаходу', field6Value: 'ТОВ «Авіа-Спецтехніка» / д.т.н. Бондар В.М., інж. Кравченко О.П.',
    notesText: 'Провести патентний пошук за світовими патентними базами на патентну чистоту. Сформулювати формулу винаходу (Claims) з 1 незалежним та 4 залежними пунктами, підготувати опис технічного результату та сформувати офіційну Заяву на видачу патенту України у Word (.docx) за вимогами УКРНОІВІ.'
  },
  'iso-17025-lab-accreditor': {
    field1LabelUk: 'Назва випробувальної лабораторії & Підприємство', field1Value: 'Випробувальний центр якості металів та сплавів ТОВ «Промсталь»',
    field2LabelUk: 'Сфера акредитації (Методи & ДСТУ)', field2Value: 'Механічні випробування на розрив (ДСТУ ISO 6892-1) та спектральний аналіз',
    field3LabelUk: 'Стандарт системи управління якістю', field3Value: 'ДСТУ ISO/IEC 17025:2019 (Загальні вимоги до компетентності)',
    field4LabelUk: 'Орган акредитації & Статус', field4Value: 'Національне агентство з акредитації України (НААУ) / Первинна акредитація',
    field5LabelUk: 'Метрологічне забезпечення & Калібрування', field5Value: 'Розривна машина ЗМ-40К (Акт калібрування Укрметртестстандарт 2026)',
    field6LabelUk: 'Результати міжлабораторних порівнянь (МЛП)', field6Value: 'Участь у 2 програмах МЛП (zIndex < 1.2 — задовільно)',
    notesText: 'Провести комплексний аудит Настанови з якості випробувальної лабораторії на відповідність ДСТУ ISO/IEC 17025:2019. Перевірити простежуваність вимірювань, розрахувати розширену невизначеність U (k=2, 95% CI) та підготувати Чек-лист та Настанову з якості у Word (.docx) для подачі в НААУ.'
  },
  'nddkr-report-constructor': {
    field1LabelUk: 'Тема НДДКР & Номер держреєстрації', field1Value: 'Розробка високоефективного теплообмінника для модульних котелень (№ ДР 0124U10492)',
    field2LabelUk: 'Замовник НДДКР & Головний виконавець', field2Value: 'Міністерство стратегічних галузей промисловості України / ДП «НДІ Теплофізики»',
    field3LabelUk: 'Стандарт оформлення звіту', field3Value: 'ДСТУ 3008:2015 «Звіти у сфері науки і техніки. Структура та правила оформлення»',
    field4LabelUk: 'Термін виконання & Кошторис (грн)', field4Value: '12 місяців (01.2025–12.2025) / 2 800 000.00 грн',
    field5LabelUk: 'Основні конструкторські рішення & Етапи', field5Value: 'Етап 1: Математична модель, Етап 2: Робоче креслення, Етап 3: Випробування дослідного зразка',
    field6LabelUk: 'Керівник робіт & Головний конструктор', field6Value: 'д.т.н., професор Коваленко С.М. / головний конструктор Сидоренко В.А.',
    notesText: 'Сформувати підсумковий Звіт про НДДКР строго за структурою ДСТУ 3008:2015. Описати реферат, технічне завдання, аналіз аналогів, розрахунок кошторису видатків по статтях (ФОП, матеріали, спецустаткування) та сформувати готовий Звіт з НДДКР у Word (.docx) за ДСТУ 4163:2020.'
  },
  'r-python-data-statistician': {
    field1LabelUk: 'Назва випробування & Джерело даних', field1Value: 'Порівняльне випробування зносостійкості 4 типів захисних покриттів',
    field2LabelUk: 'Обсяг експериментальної вибірки (Записів)', field2Value: '320 вимірів (4 групи по 80 зразків, завантажено CSV/XLSX)',
    field3LabelUk: 'Необхідні статистичні критерії', field3Value: 'Перевірка нормальності Shapiro-Wilk, ANOVA, post-hoc Tukey HSD, p-value',
    field4LabelUk: 'Рівень значущості & Довірчий інтервал', field4Value: 'p < 0.01 (Довірчий інтервал 99% CI)',
    field5LabelUk: 'Формат вихідної візуалізації (Графіки)', field5Value: 'Побудова Boxplot з точками даних та маркуванням значущості p-value',
    field6LabelUk: 'Скрипт обробки (R tidyverse / Python scipy)', field6Value: 'Python (Pandas, SciPy.stats, Seaborn, Matplotlib)',
    notesText: 'Провести повний статистичний аналіз експериментальних даних випробування зносостійкості покриттів. Оцінити нормальність розподілу, провести однофакторний дисперсійний аналіз (ANOVA), розрахувати апостеріорний критерій Тьюкі (Tukey HSD), побудувати діаграми Boxplot та сформувати готовий Статистичний Висновок у Word (.docx) та Excel (.xlsx).'
  },
  'tech-transfer-commercializer': {
    field1LabelUk: 'Назва технології / R&D розробки', field1Value: 'Компактна система водоочищення на основі нанофільтраційних мембран',
    field2LabelUk: 'Рівень технологічної готовності (TRL)', field2Value: 'TRL 6 (Прототип випробувано в реальних експлуатаційних умовах)',
    field3LabelUk: 'Правовласник & Патенти розробки', field3Value: 'Інститут хімії НАН України (Патент України № 152840)',
    field4LabelUk: 'Оціночний ринковий потенціал (TAM / SAM)', field4Value: 'TAM: $45 млн (Обладнання водоочищення), SAM: $8.5 млн (Україна & Східна Європа)',
    field5LabelUk: 'Вид ліцензійної угоди для укладення', field5Value: 'Виключна ліцензія на використання з виплатою роялті 4.5% від продажів',
    field6LabelUk: 'Оціночна вартість майнових прав (грн / USD)', field6Value: '3 500 000.00 грн (Метод звільнених від роялті дисконтованих грошових потоків)',
    notesText: 'Оцінити рівень TRL R&D розробки системи водоочищення, розрахувати фінансову вартість майнових прав інтелектуальної власності, скласти Паспорт технології для інвесторів та розробити офіційний Ліцензійний Договір про передачу прав у Word (.docx) за ДСТУ 4163:2020.'
  },
  'pharma-care-assistant': {
    field1LabelUk: 'Торговельне найменування ЛЗ / МНН', field1Value: 'Амлодипін 5 мг (МНН: Amlodipine) / Таблетки',
    field2LabelUk: 'Реєстраційний номер у ДРЛЗ України', field2Value: 'UA/10492/01/01 (Термін дії реєстраційного посвідчення: безстроково)',
    field3LabelUk: 'Одночасно призначені ЛЗ (Взаємодія)', field3Value: 'Симвастатин 40 мг, Аспірин Кардіо 100 мг, Лізиноприл 10 мг',
    field4LabelUk: 'Виявлені ризики міжлікарської взаємодії', field4Value: 'Підвищення ризику міопатії при комбінації Амлодипіну > 10 мг та Симвастатину > 20 мг',
    field5LabelUk: 'Підбір генеричних аналогів (За МНН)', field5Value: 'Норваск (Оригінал), Амлодипін-Фармак, Норвадін, Неокарділ',
    field6LabelUk: 'Фармацевтична опіка пацієнта (Симптоми)', field6Value: 'Артеріальна гіпертензія / Прийом строго вранці, запивати водою (не грейпфрутовим соком)',
    notesText: 'Провести детальний пошук за Державним реєстром лікарських засобів України (ДРЛЗ). Оцінити ризики міжлікарської взаємодії, перевірити протипоказання, підібрати еквівалентні генеричні препарати та сформувати Картку фармацевтичної опіки пацієнта у Word (.docx) за ДСТУ 4163:2020.'
  },
  'drug-compat-checker-ai': {
    field1LabelUk: 'Призначений перелік лікарських засобів (МНН / Торгові назви)', 
    field1Value: 'Варфарин (Warfarin 2.5 мг) + Ацетилосаліцилова кислота (Магнікор 75 мг) + Ібупрофен (Ibuprofen 400 мг) + Омепразол (Omeprazole 20 мг)',
    field2LabelUk: 'Коди АТХ / Реєстраційні номери ДЛУ України', 
    field2Value: 'B01AA03 (Варфарин), B01AC06 (Аспірин), M01AE01 (Ібупрофен), A02BC01 (Омепразол)',
    field3LabelUk: 'Профіль пацієнта (Вік, супутні діагнози, алергії)', 
    field3Value: 'Чоловік 64 роки. Діагнози: ІХС, фібриляція передсердь, остеоартрит колінного суглоба. ШКФ = 55 мл/хв.',
    field4LabelUk: 'Рівень ризику взаємодії (Клінічна класифікація)', 
    field4Value: 'Високий / Критичний (Поєднання перорального антикоагулянта + антиагреганта + НПЗЗ)',
    field5LabelUk: 'Нормативна база & Довідники для перевірки', 
    field5Value: 'Державний реєстр лікарських засобів України (ДЛУ), Клінічні протоколи МОЗ України, BNF, FDA Drug Interactions',
    field6LabelUk: 'Мета фармакологічного аудиту', 
    field6Value: 'Виявлення ризиків шлунково-кишкової кровотечі, антагонізму/синергізму, коригування схем прийому та підбір безпечних альтернатив',
    notesText: 'Провести комплексний перехресний аналіз міжлікарських взаємодій для наданого переліку препаратів. Оцінити фармакодинамічні та фармакокінетичні ризики, перевірити відповідність реєстраційним даним ДЛУ та клінічним протоколам МОЗ України, запропонувати безпечну альтернативну схему лікування та сформувати Офіційну Картку Фармако-Сумісності у Word (.docx) за ДСТУ 4163:2020.'
  },
  'drug-compat-checker': {
    field1LabelUk: 'Призначений перелік лікарських засобів (МНН / Торгові назви)', 
    field1Value: 'Варфарин (Warfarin 2.5 мг) + Ацетилосаліцилова кислота (Магнікор 75 мг) + Ібупрофен (Ibuprofen 400 мг) + Омепразол (Omeprazole 20 мг)',
    field2LabelUk: 'Коди АТХ / Реєстраційні номери ДЛУ України', 
    field2Value: 'B01AA03 (Варфарин), B01AC06 (Аспірин), M01AE01 (Ібупрофен), A02BC01 (Омепразол)',
    field3LabelUk: 'Профіль пацієнта (Вік, супутні діагнози, алергії)', 
    field3Value: 'Чоловік 64 роки. Діагнози: ІХС, фібриляція передсердь, остеоартрит колінного суглоба. ШКФ = 55 мл/хв.',
    field4LabelUk: 'Рівень ризику взаємодії (Клінічна класифікація)', 
    field4Value: 'Високий / Критичний (Поєднання перорального антикоагулянта + антиагреганта + НПЗЗ)',
    field5LabelUk: 'Нормативна база & Довідники для перевірки', 
    field5Value: 'Державний реєстр лікарських засобів України (ДЛУ), Клінічні протоколи МОЗ України, BNF, FDA Drug Interactions',
    field6LabelUk: 'Мета фармакологічного аудиту', 
    field6Value: 'Виявлення ризиків шлунково-кишкової кровотечі, антагонізму/синергізму, коригування схем прийому та підбір безпечних альтернатив',
    notesText: 'Провести комплексний перехресний аналіз міжлікарських взаємодій для наданого переліку препаратів. Оцінити фармакодинамічні та фармакокінетичні ризики, перевірити відповідність реєстраційним даним ДЛУ та клінічним протоколам МОЗ України, запропонувати безпечну альтернативну схему лікування та сформувати Офіційну Картку Фармако-Сумісності у Word (.docx) за ДСТУ 4163:2020.'
  },
  'dls-pharma-verifier': {
    field1LabelUk: 'Серія ЛЗ & Торговельне найменування', field1Value: 'Серія 40224А / Но-Шпа 40 мг таблетки (Drotaverine)',
    field2LabelUk: 'Виробник & Сертифікат якості', field2Value: 'Санофі Завод Хіноїн (Угорщина) / Сертифікат ISO/IEC 17025',
    field3LabelUk: 'Розпорядження ДЛС (№ та дата)', field3Value: 'Розпорядження ДЛС № 1402-1.2/02.0-18 від 10.02.2026',
    field4LabelUk: 'Термін придатності & Умови зберігання', field4Value: '04.2028 / Зберігання при т-рі не вище +25°C у сухому місці',
    field5LabelUk: 'Аптечний заклад / Заявник', field5Value: 'Аптека № 5 ТОВ «Фарма-Світ» (м. Київ, вул. Хрещатик 12)',
    field6LabelUk: 'Статус дозволу ДЛС', field6Value: 'В обігу (Відсутній у реєстрах заборонених чи вилучених серій)',
    notesText: 'Провести перевірку серії лікарського засобу за розпорядженнями Держлікслужби України (ДЛС). Перевірити відповідність сертифіката якості вимогам ISO/IEC 17025, контроль термінів придатності та умов зберігання (холодовий ланцюг) з формуванням Акта перевірки у Word (.docx) за ДСТУ 4163:2020.'
  },
  'pharmacy-licensing-auditor': {
    field1LabelUk: 'Назва Аптечного закладу & Адреса', field1Value: 'Аптека № 1 ТОВ «Мед-Гарант» (м. Львів, вул. Городоцька 45)',
    field2LabelUk: 'Площа приміщень (м²) & Зони', field2Value: 'Загальна площа: 62 м² (Торговий зал 28 м², Матеріальна 18 м², Побутова 16 м²)',
    field3LabelUk: 'Контроль температури та вологості', field3Value: 'Електронний гігрометр ТМТ-2 / Температурні журнали ведуться щоденно',
    field4LabelUk: 'Ліцензійні вимоги Постанови № 929', field4Value: 'Забезпечено безперешкодний пандус, касовий апарат РРО/ПРРО, доступ до ДРЛЗ',
    field5LabelUk: 'Облік підконтрольних речовин (Наркотичні ЛЗ)', field5Value: 'Сейф 3 класу зламостійкості з підключенням до пульту ДСО МВС',
    field6LabelUk: 'Кваліфікація завідувача та фармацевтів', field6Value: 'Завідувач: провізор вищої категорії, сертифікат проходження атестації 2025 р.',
    notesText: 'Провести комплексний ліцензійний аудит аптечного закладу відповідно до Постанови КМУ № 929. Перевірити санітарно-гігієнічний стан приміщень, вимірювальне обладнання, ведення температурних журналів, умови зберігання підконтрольних речовин та згенерувати Акт ліцензійного аудиту у Word (.docx) за ДСТУ 4163:2020.'
  },
  'dostupni-liky-reimbursement': {
    field1LabelUk: 'Перелік е-Рецептів (Коди НСЗУ)', field1Value: 'Е-Рецепт № 0000-8492-1049-2041 (Програма «Доступні Ліки» - Серцево-судинні)',
    field2LabelUk: 'Реєстр ЛЗ підлягаючих реімбурсації', field2Value: 'Амлодипін 5 мг (Доплата НСЗУ 100% — БЕЗОПЛАТНО для пацієнта)',
    field3LabelUk: 'Розрібне доплачування пацієнта & Доплата НСЗУ', field3Value: 'Сума доплати пацієнта: 0.00 грн / Відшкодування НСЗУ: 48.50 грн за упаковку',
    field4LabelUk: 'Граничні надбавки (Постанова № 333)', field4Value: 'Оптова надбавка 10%, Роздрібна надбавка 15% (дотримано граничні межі)',
    field5LabelUk: 'Аптечний заклад / ЄДРПОУ', field5Value: 'ТОВ «Аптека НСЗУ Партнер» (ЄДРПОУ 38491049, Договір з НСЗУ № 84/2025)',
    field6LabelUk: 'Звітний період', field6Value: 'Січень–Лютий 2026 року (Зведена звітність за місяць)',
    notesText: 'Провести аудит електронних рецептів за програмою «Доступні ліки» НСЗУ. Розрахувати граничні надбавки, суми доплати пацієнта та реімбурсації з боку НСЗУ за Реєстром граничних цін та згенерувати Звіт для НСЗУ у Word (.docx) та Excel (.xlsx) за ДСТУ 4163:2020.'
  },
  'gdp-gsp-warehouse-auditor': {
    field1LabelUk: 'Назва аптечного складу / Дистриб\'ютора', field1Value: 'Аптечний склад № 2 ПрАТ «Фарма-Логістик» (м. Київ, вул. Промислова 8)',
    field2LabelUk: 'Температурні зони складського комплексу', field2Value: 'Зона А: +15...+25°C (1200 м²), Холодильна камера Б: +2...+8°C (180 м²)',
    field3LabelUk: 'Перелік СОП / SOP системи якості GDP', field3Value: 'SOP-01 (Прийом), SOP-02 (Зберігання), SOP-03 (Відвантаження), SOP-04 (Відкликання)',
    field4LabelUk: 'Валідація транспорту & Холодовий ланцюг', field4Value: '3 рефрижератори з термореєстраторами Testo 174T (Калібрування 2025)',
    field5LabelUk: 'Картування температури та вологості', field5Value: 'Проведено картування 24 датчиками за 7 днів (критичні гарячі/холодні точки визначено)',
    field6LabelUk: 'Стандарт сертифікації', field6Value: 'Good Distribution Practice (GDP EU Guidelines / Наказ МОЗ № 95)',
    notesText: 'Розробити Стандартні операційні процедури (СОП / SOP) фармскладу за вимогами GDP/GSP. Провести аудит температурного картування, контроль «холодового ланцюга» при транспортуванні та сформувати Звіт з валідації та СОП у Word (.docx) та Excel (.xlsx) за ДСТУ 4163:2020.'
  },
  'pharma-advisor': {
    field1LabelUk: 'Торговельне найменування ЛЗ / МНН', field1Value: 'Амлодипін + Вальсартан (Ексфорж / Вамлосет 5/160 мг)',
    field2LabelUk: 'Код АТХ (ATC) / Реєстрація в ДЛУ', field2Value: 'C09DX01 (Антагоністи ангіотензину II та блокатори кальцієвих каналів)',
    field3LabelUk: 'Фармакодинаміка & Механізм дії', field3Value: 'Комбінація дигідропіридинового БКК та блокатора рецепторів АТ-1',
    field4LabelUk: 'Показання, схема дозування та коригування', field4Value: 'Артеріальна гіпертензія 2-3 ст. 1 табл. 1 раз на добу вранці',
    field5LabelUk: 'Протипоказання, побічні дії та взаємодії', field5Value: 'Вагітність, тяжка печінкова недостатність, обструктивні розлади',
    field6LabelUk: 'Правила відпуску & Наявність у програмі «Доступні ліки»', field6Value: 'За рецептом / Включено до реєстру реімбурсації',
    notesText: 'Сформувати вичерпний фармакологічний довідник лікарського засобу за даними Державного реєстру лікарських засобів України (ДЛУ/ДРЛЗ), нормативів МОЗ, BNF та FDA. Описати фармакокінетику, взаємодію, генеричні аналоги та правила безпечного застосування у Word (.docx) за ДСТУ 4163:2020.'
  },
  'pharma-dlu-compatibility-checker': {
    field1LabelUk: 'Призначений перелік лікарських засобів (МНН / Торгові назви)', 
    field1Value: 'Варфарин (Warfarin 2.5 мг) + Ацетилосаліцилова кислота (Магнікор 75 мг) + Ібупрофен (Ibuprofen 400 мг) + Омепразол (Omeprazole 20 мг)',
    field2LabelUk: 'Коди АТХ / Реєстраційні номери ДЛУ України', 
    field2Value: 'B01AA03 (Варфарин), B01AC06 (Аспірин), M01AE01 (Ібупрофен), A02BC01 (Омепразол)',
    field3LabelUk: 'Профіль пацієнта (Вік, супутні діагнози, алергії)', 
    field3Value: 'Чоловік 64 роки. Діагнози: ІХС, фібриляція передсердь, остеоартрит колінного суглоба. ШКФ = 55 мл/хв.',
    field4LabelUk: 'Рівень ризику взаємодії (Клінічна класифікація)', 
    field4Value: 'Високий / Критичний (Поєднання перорального антикоагулянта + антиагреганта + НПЗЗ)',
    field5LabelUk: 'Нормативна база & Довідники для перевірки', 
    field5Value: 'Державний реєстр лікарських засобів України (ДЛУ), Клінічні протоколи МОЗ України, BNF, FDA Drug Interactions',
    field6LabelUk: 'Мета фармакологічного аудиту', 
    field6Value: 'Виявлення ризиків шлунково-кишкової кровотечі, антагонізму/синергізму, коригування схем прийому та підбір безпечних альтернатив',
    notesText: 'Провести комплексний перехресний аналіз міжлікарських взаємодій для наданого переліку препаратів. Оцінити фармакодинамічні та фармакокінетичні ризики, перевірити відповідність реєстраційним даним ДЛУ та клінічним протоколам МОЗ України, запропонувати безпечну альтернативну схему лікування та сформувати Офіційну Картку Фармако-Сумісності у Word (.docx) за ДСТУ 4163:2020.'
  },
  'pharma-interaction-checker': {
    field1LabelUk: 'Призначений перелік лікарських засобів (МНН / Торгові назви)', 
    field1Value: 'Варфарин (Warfarin 2.5 мг) + Ацетилосаліцилова кислота (Магнікор 75 мг) + Ібупрофен (Ibuprofen 400 мг) + Омепразол (Omeprazole 20 мг)',
    field2LabelUk: 'Коди АТХ / Реєстраційні номери ДЛУ України', 
    field2Value: 'B01AA03 (Варфарин), B01AC06 (Аспірин), M01AE01 (Ібупрофен), A02BC01 (Омепразол)',
    field3LabelUk: 'Профіль пацієнта (Вік, супутні діагнози, алергії)', 
    field3Value: 'Чоловік 64 роки. Діагнози: ІХС, фібриляція передсердь, остеоартрит колінного суглоба. ШКФ = 55 мл/хв.',
    field4LabelUk: 'Рівень ризику взаємодії (Клінічна класифікація)', 
    field4Value: 'Високий / Критичний (Поєднання перорального антикоагулянта + антиагреганта + НПЗЗ)',
    field5LabelUk: 'Нормативна база & Довідники для перевірки', 
    field5Value: 'Державний реєстр лікарських засобів України (ДЛУ), Клінічні протоколи МОЗ України, BNF, FDA Drug Interactions',
    field6LabelUk: 'Мета фармакологічного аудиту', 
    field6Value: 'Виявлення ризиків шлунково-кишкової кровотечі, антагонізму/синергізму, коригування схем прийому та підбір безпечних альтернатив',
    notesText: 'Провести комплексний перехресний аналіз міжлікарських взаємодій для наданого переліку препаратів. Оцінити фармакодинамічні та фармакокінетичні ризики, перевірити відповідність реєстраційним даним ДЛУ та клінічним протоколам МОЗ України, запропонувати безпечну альтернативну схему лікування та сформувати Офіційну Картку Фармако-Сумісності у Word (.docx) за ДСТУ 4163:2020.'
  },
  'pharma-quality-certificate-verifier': {
    field1LabelUk: 'Серія ЛЗ & Торговельне найменування', field1Value: 'Серія 40224А / Но-Шпа 40 мг таблетки (Drotaverine)',
    field2LabelUk: 'Виробник & Сертифікат якості', field2Value: 'Санофі Завод Хіноїн (Угорщина) / Сертифікат ISO/IEC 17025',
    field3LabelUk: 'Розпорядження ДЛС (№ та дата)', field3Value: 'Розпорядження ДЛС № 1402-1.2/02.0-18 від 10.02.2026',
    field4LabelUk: 'Термін придатності & Умови зберігання', field4Value: '04.2028 / Зберігання при т-рі не вище +25°C у сухому місці',
    field5LabelUk: 'Аптечний заклад / Заявник', field5Value: 'Аптека № 5 ТОВ «Фарма-Світ» (м. Київ, вул. Хрещатик 12)',
    field6LabelUk: 'Статус дозволу ДЛС', field6Value: 'В обігу (Відсутній у реєстрах заборонених чи вилучених серій)',
    notesText: 'Провести перевірку серії лікарського засобу за розпорядженнями Держлікслужби України (ДЛС). Перевірити відповідність сертифіката якості вимогам ISO/IEC 17025, контроль термінів придатності та умов зберігання (холодовий ланцюг) з формуванням Акта перевірки у Word (.docx) за ДСТУ 4163:2020.'
  },
  'pharma-nhsu-reimbursement-auditor': {
    field1LabelUk: 'Перелік е-Рецептів (Коди НСЗУ)', field1Value: 'Е-Рецепт № 0000-8492-1049-2041 (Програма «Доступні Ліки» - Серцево-судинні)',
    field2LabelUk: 'Реєстр ЛЗ підлягаючих реімбурсації', field2Value: 'Амлодипін 5 мг (Доплата НСЗУ 100% — БЕЗОПЛАТНО для пацієнта)',
    field3LabelUk: 'Розрібне доплачування пацієнта & Доплата НСЗУ', field3Value: 'Сума доплати пацієнта: 0.00 грн / Відшкодування НСЗУ: 48.50 грн за упаковку',
    field4LabelUk: 'Граничні надбавки (Постанова № 333)', field4Value: 'Оптова надбавка 10%, Роздрібна надбавка 15% (дотримано граничні межі)',
    field5LabelUk: 'Аптечний заклад / ЄДРПОУ', field5Value: 'ТОВ «Аптека НСЗУ Партнер» (ЄДРПОУ 38491049, Договір з НСЗУ № 84/2025)',
    field6LabelUk: 'Звітний період', field6Value: 'Січень–Лютий 2026 року (Зведена звітність за місяць)',
    notesText: 'Провести аудит електронних рецептів за програмою «Доступні ліки» НСЗУ. Розрахувати граничні надбавки, суми доплати пацієнта та реімбурсації з боку НСЗУ за Реєстром граничних цін та згенерувати Звіт для НСЗУ у Word (.docx) та Excel (.xlsx) за ДСТУ 4163:2020.'
  }
};

const LOADING_STEPS_UK = [
  "Ініціалізація мультиагентної команди Наукових та Фармацевтичних Експертів...",
  "Обробка даних ДЛУ / ДРЛЗ, нормативів МОЗ, ДФУ, BNF, FDA та баз Scopus/WoS...",
  "Проведення фармакологічного аудиту, оцінки взаємодій та нормативного комплаєнсу...",
  "Оформлення фармакотерапевтичної документації за ДСТУ 4163:2020 та протоколами МОЗ...",
  "Формування підсумкового Фармацевтичного / Наукового Звіту за стандартами ДСТУ..."
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <GraduationCap className={className} />;
  return <IconComponent className={className} />;
};

export default function ScienceAgentView({
  agentId,
  language,
  onBack,
  onGoHome
}: ScienceAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const isPharma = agentId.startsWith('pharma-') || 
                   agentId.startsWith('drug-') || 
                   agentId.startsWith('dls-') || 
                   agentId.startsWith('pharmacy-') || 
                   agentId.startsWith('dostupni-') || 
                   agentId.startsWith('gdp-') ||
                   agentId.includes('pharma') ||
                   agentId.includes('drug');
  const agentConfig = agentsConfig.find((a: any) => a.id === agentId) || {
    nameUk: 'Науковий / Фармацевтичний Аналітичний Модуль',
    nameEn: 'Science / Pharma AI Agent',
    descriptionUk: 'Аналіз, дослідження та фармакологічний комплаєнс.',
    descriptionEn: 'Scientific research and pharmaceutical compliance audit.',
    icon: 'Pill',
    categoryUk: 'ФАРМАЦЕВТИКА & НАУКА',
    categoryEn: 'PHARMA & SCIENCE'
  };

  const getDemoForAgent = (id: string) => {
    return DEFAULT_SCIENCE_DEMO[id] ||
      (id.includes('compat') || id.includes('dlu') || id.includes('interaction') ? DEFAULT_SCIENCE_DEMO['drug-compat-checker-ai'] :
       id.includes('care') || id.includes('drlz') ? DEFAULT_SCIENCE_DEMO['pharma-care-assistant'] :
       id.includes('advisor') || id.includes('pharma') || id.includes('drug') || id.includes('medic') || id.includes('liky') ? DEFAULT_SCIENCE_DEMO['pharma-advisor'] :
       id.includes('licens') ? DEFAULT_SCIENCE_DEMO['pharmacy-licensing-auditor'] :
       id.includes('reimburse') || id.includes('dostupni') || id.includes('nhsu') ? DEFAULT_SCIENCE_DEMO['dostupni-liky-reimbursement'] :
       id.includes('gdp') || id.includes('warehouse') ? DEFAULT_SCIENCE_DEMO['gdp-gsp-warehouse-auditor'] :
       DEFAULT_SCIENCE_DEMO['science-grant-proposal-writer']);
  };

  const defaultDemo = useMemo(() => getDemoForAgent(agentId), [agentId]);

  const [field1, setField1] = useState(getFieldValue(defaultDemo, 1, language));
  const [field2, setField2] = useState(getFieldValue(defaultDemo, 2, language));
  const [field3, setField3] = useState(getFieldValue(defaultDemo, 3, language));
  const [field4, setField4] = useState(getFieldValue(defaultDemo, 4, language));
  const [field5, setField5] = useState(getFieldValue(defaultDemo, 5, language));
  const [field6, setField6] = useState(getFieldValue(defaultDemo, 6, language));
  const [notesText, setNotesText] = useState(getNotesText(defaultDemo, language));

  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; size: number; text: string }>>([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [reportResult, setReportResult] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [availableSkills, setAvailableSkills] = useState<Array<{ name: string; categoryUk: string }>>([]);
  const [selectedSkill, setSelectedSkill] = useState<string>('');

  useEffect(() => {
    fetch('/api/scientific-skills/list')
      .then(res => res.json())
      .then(data => {
        if (data.skills) setAvailableSkills(data.skills);
      })
      .catch(() => {});
  }, []);

  // Update input fields dynamically when agentId or language changes
  useEffect(() => {
    setField1(getFieldValue(defaultDemo, 1, language));
    setField2(getFieldValue(defaultDemo, 2, language));
    setField3(getFieldValue(defaultDemo, 3, language));
    setField4(getFieldValue(defaultDemo, 4, language));
    setField5(getFieldValue(defaultDemo, 5, language));
    setField6(getFieldValue(defaultDemo, 6, language));
    setNotesText(getNotesText(defaultDemo, language));
    setReportResult(null);
  }, [defaultDemo, language]);

  useEffect(() => {
    if (reportResult && typeof reportResult === 'string' && reportResult.trim().length > 0 && !reportResult.startsWith('⚠️ ПОМИЛКА')) {
      autoDownloadReport(reportResult, agentConfig.nameUk || 'Науковий Звіт', agentId);
    }
  }, [reportResult]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFile(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/extract-text', { method: 'POST', body: formData });

        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: data.text || '' }]);
        } else {
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: `[Файл ${file.name} додано]` }]);
        }
      }
    } catch (err) {
      console.error('File upload error:', err);
    } finally {
      setIsReadingFile(false);
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRunAnalysis = async () => {
    setIsProcessing(true);
    setReportResult(null);
    setLoadingStepIndex(0);

    const stepInterval = setInterval(() => {
      setLoadingStepIndex(prev => (prev < LOADING_STEPS_UK.length - 1 ? prev + 1 : prev));
    }, 2500);

    try {
      const combinedDocText = attachedFiles.map(f => `--- ФАЙЛ: ${f.name} ---\n${f.text}`).join('\n\n');
      
      const payload = {
        agentId,
        userQuery: notesText,
        language,
        scientificSkill: selectedSkill || undefined,
        parameters: {
          [defaultDemo.field1LabelUk]: field1,
          [defaultDemo.field2LabelUk]: field2,
          [defaultDemo.field3LabelUk]: field3,
          [defaultDemo.field4LabelUk]: field4,
          [defaultDemo.field5LabelUk]: field5,
          [defaultDemo.field6LabelUk]: field6,
          "Додаткові документи": combinedDocText
        }
      };

      const res = await fetch('/api/generate-agent-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.report || data.content)) {
        setReportResult(data.report || data.content || 'Звіт успішно згенеровано!');
      } else {
        const errorText = data.error || data.reason || `Не вдалося отримати відповідь бекенду (HTTP ${res.status}).`;
        setReportResult(`⚠️ ПОМИЛКА ГЕНЕРАЦІЇ: ${errorText}`);
      }
    } catch (err: any) {
      setReportResult(`⚠️ ПОМИЛКА: ${err.message || 'Збій мережі'}`);
    } finally {
      clearInterval(stepInterval);
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono">
      {/* Top Header Navigation */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            🏠 {isPl ? 'Strona Główna' : isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>
              {isPharma 
                ? (isPl ? '← Powrót do Farmacji' : isUk ? '← До Фармацевтичного Центру' : '← Back to Pharma Hub')
                : (isPl ? '← Powrót do Nauki' : isUk ? '← До Наукового Центру' : '← Back to Science Hub')
              }
            </span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          {isPharma ? (
            <>
              <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-teal-500/20 text-teal-300 border border-teal-400/40 rounded-md flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-teal-300" />
                💊 {getLocalizedAgentCategory(agentConfig, language)}
              </span>
              <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
                {isPl ? 'ChPL • URPL • EMA • BNF • FDA' : 'ДЛУ • ДФУ • МОЗ • BNF • FDA'}
              </span>
            </>
          ) : (
            <>
              <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded-md">
                🎓 {getLocalizedAgentCategory(agentConfig, language)}
              </span>
              <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-md">
                Scopus • WoS • НАЗЯВО • ДСТУ 8302
              </span>
            </>
          )}
        </div>
      </div>

      {/* Hero Banner */}
      <div className={`bg-slate-950 border ${isPharma ? 'border-teal-500/40 shadow-teal-950/20' : 'border-purple-500/40 shadow-purple-950/20'} rounded-2xl p-6 sm:p-8 text-white shadow-2xl space-y-4`}>
        <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className={`p-4 ${isPharma ? 'bg-teal-950/80 border-teal-500/40 text-teal-400' : 'bg-purple-950/80 border-purple-500/40 text-purple-400'} border rounded-2xl shrink-0 shadow-lg`}>
            {isPharma ? <Pill className="w-8 h-8" /> : <AgentIcon name={agentConfig.icon} className="w-8 h-8" />}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2.5 py-0.5 text-[9px] font-extrabold uppercase ${isPharma ? 'bg-teal-500/30 text-teal-200 border-teal-400/40' : 'bg-purple-500/30 text-purple-200 border-purple-400/40'} border rounded`}>
                {getLocalizedAgentCategory(agentConfig, language)}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wide text-white">
              {getLocalizedAgentName(agentConfig, language)}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
              {getLocalizedAgentDesc(agentConfig, language)}
            </p>
          </div>
        </div>
      </div>

      {/* Main Parameters Form */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-6 text-white shadow-xl">
        <h3 className={`text-sm font-extrabold uppercase tracking-wider ${isPharma ? 'text-teal-300' : 'text-purple-300'} flex items-center gap-2 border-b border-slate-800 pb-3`}>
          {isPharma ? <Pill className="w-4 h-4 text-teal-400" /> : <GraduationCap className="w-4 h-4 text-purple-400" />}
          <span>
            {isPl
              ? (isPharma ? 'PARAMETRY WEJŚCIOWE ANALIZY FARMACEUTYCZNEJ I LEKÓW:' : 'PARAMETRY WEJŚCIOWE BADAŃ NAUKOWYCH / PUBLIKACJI:')
              : isUk 
              ? (isPharma ? 'ВХІДНІ ПАРАМЕТРИ ФАРМАЦЕВТИЧНОГО АНАЛІЗУ ТА ПРЕПАРАТІВ:' : 'ВХІДНІ ПАРАМЕТРИ НАУКОВОГО ДОСЛІДЖЕННЯ / СТАТТІ:') 
              : (isPharma ? 'PHARMACEUTICAL & DRUG INPUT PARAMETERS:' : 'RESEARCH PARAMETERS:')
            }
          </span>
        </h3>

        {/* 🔬 Scientific Skill & MCP Context-Mode Selector */}
        <div className="bg-slate-950 border border-purple-500/40 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-purple-300 uppercase tracking-wide flex items-center gap-1.5">
                <Microscope className="w-4 h-4 text-purple-400" />
                <span>{isUk ? 'Спеціалізований науковий скіл (177 Skills):' : 'Specialized Scientific Skill (177 Skills):'}</span>
              </span>
              <span className="px-2 py-0.5 text-[9px] bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded flex items-center gap-1 font-bold">
                <Activity className="w-2.5 h-2.5 text-emerald-400 animate-pulse" />
                <span>MCP Context-Mode: ON</span>
              </span>
            </div>
            <p className="text-[11px] text-slate-400">
              {isUk 
                ? 'Оберіть процедурний протокол K-Dense або залиште авто-визначення за темою' 
                : 'Select procedural standard or leave auto-matching'}
            </p>
          </div>

          <select
            value={selectedSkill}
            onChange={(e) => setSelectedSkill(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400 max-w-sm cursor-pointer font-mono"
          >
            <option value="">{isUk ? '⚡ [Автоматичний підбір під тему]' : '⚡ [Auto-detect best match]'}</option>
            {availableSkills.map((sk) => (
              <option key={sk.name} value={sk.name}>
                {sk.name} ({sk.categoryUk?.split(',')[0] || 'Наука'})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">1. {getFieldLabel(defaultDemo, 1, language)}</label>
            <input
              type="text"
              list="datalist-sci-f1"
              value={field1}
              onChange={e => setField1(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz własną wartość..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className={`w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:${isPharma ? 'border-teal-500' : 'border-purple-500'} focus:outline-none font-mono`}
            />
            <datalist id="datalist-sci-f1">
              {isPharma ? (
                <>
                  <option value="Амлодипін + Вальсартан (5/160 мг)" />
                  <option value="Ксарелто (Rivaroxaban / Рівароксабан 10/20 мг)" />
                  <option value="Варфарин (Warfarin 2.5 мг) + Аспірин Кардіо 100 мг" />
                  <option value="Серія 40224А / Но-Шпа 40 мг таблетки" />
                  <option value="Е-Рецепт № 0000-8492-1049-2041 (Доступні Ліки)" />
                  <option value="Аптечний склад № 2 ПрАТ «Фарма-Логістик»" />
                </>
              ) : (
                <>
                  <option value="Horizon Europe (Cluster 4: Digital, Industry & Space)" />
                  <option value="Horizon Europe (EIC Accelerator / Breakthrough Innovation)" />
                  <option value="НФФД України (Конкурс наукових досліджень фундаментальних та прикладних)" />
                  <option value="Постанова КМУ № 44 (Порядок присудження ступеня доктора філософії)" />
                </>
              )}
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">2. {getFieldLabel(defaultDemo, 2, language)}</label>
            <input
              type="text"
              list="datalist-sci-f2"
              value={field2}
              onChange={e => setField2(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz własną wartość..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className={`w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:${isPharma ? 'border-teal-500' : 'border-purple-500'} focus:outline-none font-mono`}
            />
            <datalist id="datalist-sci-f2">
              {isPharma ? (
                <>
                  <option value="C09DX01 (Антагоністи ангіотензину II та БКК)" />
                  <option value="B01AF01 (Прямі інгібітори фактора Xa)" />
                  <option value="B01AA03 (Варфарин) / M01AE01 (Ібупрофен)" />
                  <option value="UA/10492/01/01 (Безстрокове реєстраційне посвідчення ДРЛЗ)" />
                  <option value="Санофі Завод Хіноїн (Угорщина) / Сертифікат ISO/IEC 17025" />
                  <option value="Реєстр ЛЗ, що підлягають реімбурсації НСЗУ" />
                </>
              ) : (
                <>
                  <option value="Remote Sensing of Environment (Elsevier, Scopus Q1, IF = 13.5)" />
                  <option value="Nature Communications (Springer Nature, Scopus Q1)" />
                  <option value="IEEE Transactions on Geoscience and Remote Sensing" />
                  <option value="113 Прикладна математика / 121 Інженерія програмного забезпечення" />
                </>
              )}
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">3. {getFieldLabel(defaultDemo, 3, language)}</label>
            <input
              type="text"
              list="datalist-sci-f3"
              value={field3}
              onChange={e => setField3(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz własną wartość..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className={`w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:${isPharma ? 'border-teal-500' : 'border-purple-500'} focus:outline-none font-mono`}
            />
            <datalist id="datalist-sci-f3">
              {isPharma ? (
                <>
                  <option value="1 табл. 1 раз на добу вранці (після їжі)" />
                  <option value="Симвастатин 40 мг, Аспірин Кардіо 100 мг, Лізиноприл 10 мг" />
                  <option value="Чоловік 64 роки, ІХС, ШКФ = 55 мл/хв" />
                  <option value="Розпорядження ДЛС № 1402-1.2/02.0-18 від 10.02.2026" />
                  <option value="Сума доплати пацієнта: 0.00 грн / Відшкодування: 48.50 грн" />
                  <option value="SOP-01 (Прийом), SOP-02 (Зберігання), SOP-03 (Відвантаження)" />
                </>
              ) : (
                <>
                  <option value="УКРНОІВІ (СЕВ ЗЕС - Україна)" />
                  <option value="ESPACENET (Європейське патентне відомство EPO)" />
                  <option value="WIPO PATENTSCOPE (Міжнародні заявки PCT)" />
                  <option value="Shapiro-Wilk (Тест нормальності), ANOVA / Kruskal-Wallis, p-value" />
                </>
              )}
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">4. {getFieldLabel(defaultDemo, 4, language)}</label>
            <input
              type="text"
              list="datalist-sci-f4"
              value={field4}
              onChange={e => setField4(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz własną wartość..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className={`w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:${isPharma ? 'border-teal-500' : 'border-purple-500'} focus:outline-none font-mono`}
            />
            <datalist id="datalist-sci-f4">
              {isPharma ? (
                <>
                  <option value="Високий / Критичний (Поєднання перорального антикоагулянта + НПЗЗ)" />
                  <option value="Підвищення ризику міопатії при комбінації > 10 мг та Симвастатину > 20 мг" />
                  <option value="Зберігання при т-рі не вище +25°C у сухому місці" />
                  <option value="Забезпечено безперешкодний пандус, касовий апарат РРО/ПРРО" />
                  <option value="Оптова надбавка 10%, Роздрібна надбавка 15% (Постанова № 333)" />
                </>
              ) : (
                <>
                  <option value="Патент США US10842910B2 (Прототип з кутовим кроком 0.5°)" />
                  <option value="4 доповіді на конференціях + 1 свідоцтво авторського права" />
                  <option value="CSV / Excel-таблиця експериментальної вибірки" />
                </>
              )}
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">5. {getFieldLabel(defaultDemo, 5, language)}</label>
            <input
              type="text"
              list="datalist-sci-f5"
              value={field5}
              onChange={e => setField5(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz własną wartość..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className={`w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:${isPharma ? 'border-teal-500' : 'border-purple-500'} focus:outline-none font-mono`}
            />
            <datalist id="datalist-sci-f5">
              {isPharma ? (
                <>
                  <option value="Державний реєстр лікарських засобів України (ДЛУ), МОЗ, BNF, FDA" />
                  <option value="Норваск (Оригінал), Амлодипін-Фармак, Норвадін, Неокарділ" />
                  <option value="Сейф 3 класу зламостійкості з підключенням до пульту ДСО МВС" />
                  <option value="ТОВ «Аптека НСЗУ Партнер» (ЄДРПОУ 38491049)" />
                  <option value="Картування 24 датчиками за 7 днів (гарячі/холодні точки визначено)" />
                </>
              ) : (
                <>
                  <option value="ДСТУ 8302:2015 (Інформація та документація. Бібліографічне посилання)" />
                  <option value="APA 7th Edition (American Psychological Association)" />
                  <option value="IEEE Reference Style" />
                  <option value="Chicago Manual of Style (Author-Date)" />
                </>
              )}
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">6. {getFieldLabel(defaultDemo, 6, language)}</label>
            <input
              type="text"
              list="datalist-sci-f6"
              value={field6}
              onChange={e => setField6(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz własną wartość..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className={`w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:${isPharma ? 'border-teal-500' : 'border-purple-500'} focus:outline-none font-mono`}
            />
            <datalist id="datalist-sci-f6">
              {isPharma ? (
                <>
                  <option value="Виявлення ризиків кровотечі, антагонізму та безпечних альтернатив" />
                  <option value="Прийом строго вранці, запивати водою (не грейпфрутовим соком)" />
                  <option value="В обігу (Відсутній у реєстрах заборонених чи вилучених серій)" />
                  <option value="Завідувач: провізор вищої категорії, сертифікат атестації 2025" />
                  <option value="Good Distribution Practice (GDP EU Guidelines / Наказ МОЗ № 95)" />
                </>
              ) : (
                <>
                  <option value="TRL 3 (Лабораторний прототип) → TRL 6 (Демонстрація в умовах)" />
                  <option value="Унікальність 89.4% (Unicheck / StrikePlagiarism / ДАК)" />
                  <option value="R (ggplot2, stats, tidyverse) / Python (SciPy, Seaborn)" />
                </>
              )}
            </datalist>
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">
            {isUk 
              ? (isPharma ? '7. КЛІНІЧНІ ВИМОГИ, ФАРМАКОПЕЙНІ СТАНДАРТИ ТА МЕТА АУДИТУ:' : '7. ТЕХНІЧНЕ ЗАВДАННЯ ТА НАУКОВІ ВИМОГИ:')
              : (isPharma ? '7. CLINICAL & PHARMACOPOEIA SPECIFICATIONS:' : '7. TECHNICAL & SCIENTIFIC REQUIREMENTS:')
            }
          </label>
          <textarea 
            rows={4} 
            value={notesText} 
            onChange={e => setNotesText(e.target.value)} 
            className={`w-full p-3.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:${isPharma ? 'border-teal-500' : 'border-purple-500'} focus:outline-none font-mono leading-relaxed`} 
          />
        </div>

        <div className="space-y-3 pt-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center gap-2">
              <Upload className={`w-4 h-4 ${isPharma ? 'text-teal-400' : 'text-purple-400'}`} />
              <span>
                {isUk 
                  ? (isPharma ? 'РЕЦЕПТИ, СЕРТИФІКАТИ ЯКОСТІ, ІНСТРУКЦІЇ ТА ТАБЛИЦІ (PDF / DOCX / CSV / XLSX):' : 'НАУКОВІ РУКОПИСИ, ТАБЛИЦІ ДАНИХ ТА ДЕКЛАРАЦІЇ (PDF / DOCX / CSV / XLSX):')
                  : (isPharma ? 'PRESCRIPTIONS, CERTIFICATES & DATA (PDF / DOCX / CSV / XLSX):' : 'MANUSCRIPTS, DATA TABLES & DECLARATIONS (PDF / DOCX / CSV / XLSX):')
                }
              </span>
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className={`px-4 py-2.5 ${isPharma ? 'bg-teal-950/80 hover:bg-teal-900 border-teal-500/40 text-teal-200' : 'bg-purple-950/80 hover:bg-purple-900 border-purple-500/40 text-purple-200'} border text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2 shadow-sm`}>
              <Upload className={`w-4 h-4 ${isPharma ? 'text-teal-400' : 'text-purple-400'}`} />
              <span>{isReadingFile ? 'Зчитування...' : '➕ Завантажити документи'}</span>
              <input type="file" multiple accept=".pdf,.docx,.xlsx,.csv,.json,.txt" onChange={handleFileUpload} className="hidden" />
            </label>

            {attachedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200">
                <FileCheck className={`w-3.5 h-3.5 ${isPharma ? 'text-teal-400' : 'text-purple-400'}`} />
                <span className="font-mono text-[11px] max-w-[150px] truncate">{file.name}</span>
                <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-400 p-0.5 cursor-pointer">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={handleRunAnalysis}
            disabled={isProcessing}
            className={`w-full sm:w-auto px-8 py-3.5 ${isPharma ? 'bg-teal-600 hover:bg-teal-500' : 'bg-purple-600 hover:bg-purple-500'} text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50`}
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>{LOADING_STEPS_UK[loadingStepIndex]}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>
                  {isPharma 
                    ? (isUk ? '🚀 ЗАПУСТИТИ ФАРМАКОЛОГІЧНИЙ АНАЛІЗ' : '🚀 RUN PHARMA ANALYSIS')
                    : (isUk ? '🚀 ЗАПУСТИТИ НАУКОВИЙ АНАЛІЗ' : '🚀 RUN SCIENTIFIC ANALYSIS')
                  }
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {reportResult && (
        <div className={`bg-slate-950 border ${isPharma ? 'border-teal-500/40' : 'border-purple-500/40'} rounded-2xl p-6 sm:p-8 space-y-4 text-white shadow-2xl animate-fade-in`}>
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
            <h3 className={`text-sm font-extrabold uppercase tracking-wide ${isPharma ? 'text-teal-300' : 'text-purple-300'} flex items-center gap-2`}>
              {isPharma ? <Pill className="w-4 h-4 text-teal-400" /> : <FileText className="w-4 h-4 text-purple-400" />}
              <span>
                {isPharma 
                  ? (isUk ? 'ОФІЦІЙНИЙ ФАРМАЦЕВТИЧНИЙ ВИСНОВОК / АУДИТ:' : 'OFFICIAL PHARMACEUTICAL REPORT:')
                  : (isUk ? 'ОФІЦІЙНИЙ НАУКОВО-АКАДЕМІЧНИЙ ЗВІТ:' : 'OFFICIAL SCIENTIFIC & ACADEMIC REPORT:')
                }
              </span>
            </h3>

            <div className="flex items-center gap-2">
              <TelegramExportButton
                title={agentConfig.nameUk || (isPharma ? 'Фармацевтичний Висновок' : 'Науково-Академічний Звіт')}
                content={reportResult}
                agentName={agentConfig.nameUk || (isPharma ? 'Фармацевтичний Центр' : 'Науково-Освітній Центр')}
                agentId={agentId}
                isUk={isUk}
                variant="primary"
              />

              <button
                onClick={() => {
                  navigator.clipboard.writeText(reportResult);
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 2000);
                }}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-300" />}
                <span>{isCopied ? 'Скопійовано!' : 'Копіювати висновок'}</span>
              </button>
            </div>
          </div>

          <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-5 text-xs font-mono leading-relaxed text-slate-200 whitespace-pre-wrap max-h-[600px] overflow-y-auto">
            {reportResult}
          </div>
        </div>
      )}
    </div>
  );
}
